import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook that checks whether the current user can access a given page.
 *
 * - super_admin: all pages always accessible (bypass).
 * - admin / worker / viewer: page key must be present in the user's
 *   `allowed_pages` array (populated via User Management or the
 *   SuperAdmin panel).
 */
export function usePageAccess() {
  const { user } = useAuth();

  const isSuperAdmin = user?.role === 'super_admin';

  const allowedPages: string[] = useMemo(
    () => (user as any)?.allowed_pages ?? [],
    [(user as any)?.allowed_pages],
  );

  const hasAccess = useCallback(
    (pageKey: string): boolean => {
      if (!user) return false;
      if (isSuperAdmin) return true;
      return allowedPages.includes(pageKey);
    },
    [user, isSuperAdmin, allowedPages],
  );

  return { hasAccess, allowedPages, isSuperAdmin };
}
